import React, { useState, useEffect } from "react";
import { CheckCircle, AlertCircle, XCircle, Info, X } from "lucide-react";

export type NotificationType = "success" | "error" | "warning" | "info";
export interface NotificationAction {
  label: string;
  onClick: (close: () => void) => void;
  className?: string;
}
export interface NotificationProps {
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  onClose?: () => void;
  showCloseButton?: boolean;
  className?: string;
  actions?: NotificationAction[];
}

export default function Notification({
  type,
  title,
  message,
  duration = 5000,
  onClose,
  showCloseButton = true,
  className = "",
  actions = [],
}: NotificationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  };

  if (!isVisible) return null;

  const getNotificationStyles = () => {
    const baseStyles =
      "relative overflow-hidden rounded-lg border shadow-lg transition-all duration-300 ease-in-out";
    const animationStyles = isLeaving
      ? "transform translate-x-full opacity-0"
      : "transform translate-x-0 opacity-100";

    switch (type) {
      case "success":
        return `${baseStyles} ${animationStyles} bg-green-50 border-green-200 text-green-800`;
      case "error":
        return `${baseStyles} ${animationStyles} bg-red-50 border-red-200 text-red-800`;
      case "warning":
        return `${baseStyles} ${animationStyles} bg-yellow-50 border-yellow-200 text-yellow-800`;
      case "info":
        return `${baseStyles} ${animationStyles} bg-blue-50 border-blue-200 text-blue-800`;
      default:
        return `${baseStyles} ${animationStyles} bg-gray-50 border-gray-200 text-gray-800`;
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0";

    switch (type) {
      case "success":
        return <CheckCircle className={`${iconClass} text-green-600`} />;
      case "error":
        return <XCircle className={`${iconClass} text-red-600`} />;
      case "warning":
        return <AlertCircle className={`${iconClass} text-yellow-600`} />;
      case "info":
        return <Info className={`${iconClass} text-blue-600`} />;
      default:
        return <Info className={`${iconClass} text-gray-600`} />;
    }
  };

  const getProgressBarColor = () => {
    switch (type) {
      case "success":
        return "bg-green-400";
      case "error":
        return "bg-red-400";
      case "warning":
        return "bg-yellow-400";
      case "info":
        return "bg-blue-400";
      default:
        return "bg-gray-400";
    }
  };

  return (
    <div className={`${getNotificationStyles()} ${className}`}>
      {/* Progress bar */}
      {duration > 0 && actions?.length === 0 && (
        <div className="absolute top-0 left-0 h-1 w-full bg-black bg-opacity-10">
          <div
            className={`h-full ${getProgressBarColor()} transition-all ease-linear`}
            style={{
              animation: `shrink ${duration}ms linear forwards`,
              width: "100%",
            }}
          />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">{getIcon()}</div>

          <div className="ml-3 flex-1">
            <h3 className="text-sm font-semibold">{title}</h3>
            {message && <p className="mt-1 text-sm opacity-90">{message}</p>}
            {/* Botones de acción */}
            {actions?.length > 0 && (
              <div className="mt-3 flex justify-end gap-2">
                {actions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => action.onClick(handleClose)}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      action.className ?? "bg-gray-300 text-black"
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showCloseButton && (
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={handleClose}
                className="inline-flex rounded-md p-1.5 transition-colors hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <span className="sr-only">Cerrar</span>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
}

// Toast notification component for floating notifications
export function Toast({
  type,
  title,
  message,
  duration = 4000,
  onClose,
  className = "",
}: NotificationProps) {
  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm w-full ${className}`}>
      <Notification
        type={type}
        title={title}
        message={message}
        duration={duration}
        onClose={onClose}
        showCloseButton={true}
      />
    </div>
  );
}

// Inline notification component for form feedback
export function InlineNotification({
  type,
  title,
  message,
  showCloseButton = false,
  className = "",
}: NotificationProps) {
  return (
    <Notification
      type={type}
      title={title}
      message={message}
      duration={0}
      showCloseButton={showCloseButton}
      className={className}
    />
  );
}
